import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { fmt, fmtDate } from '../../lib/constants';
import {
  generateMembersPDF, generateFinancialPDF,
  generateAttendancePDF, generateAnnualReportPDF,
  generateTrackingPDF,
  exportMembersExcel, exportFinancialsExcel, exportAttendanceExcel,
} from '../../lib/reports'; // ✅ added exports
import StatCard from '../../components/ui/StatCard'; // optional, but matches second code

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const REPORT_SECTIONS = [
  { key:'annual',      icon:'📊', label:'Annual Report',       desc:'Full year church summary' },
  { key:'members',     icon:'👥', label:'Members Report',      desc:'Membership lists and demographics' },
  { key:'financial',   icon:'💰', label:'Financial Reports',   desc:'Contributions, expenses, statements' },
  { key:'attendance',  icon:'📋', label:'Attendance Reports',  desc:'Services, headcount, Holy Communion' },
  { key:'life_events', icon:'📖', label:'Life Events',         desc:'Baptisms, confirmations, weddings, burials' },
  { key:'groups',      icon:'🙏', label:'Groups & Ministries', desc:'Group membership reports' },
  { key:'inventory',   icon:'📦', label:'Inventory Report',    desc:'Asset register and values' },
  { key:'households',  icon:'🏠', label:'Households Report',   desc:'Family households by district' },
];

// ── safe query wrapper ─────────────────────────────────────────
async function safe(query) {
  try { const r = await query; return r.data ? r : { data: [] }; }
  catch { return { data: [] }; }
}

export default function ReportsPage() {
  const [section, setSection] = useState('annual');
  const [year,    setYear]    = useState(new Date().getFullYear());
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, [year]);

  async function loadAll() {
    setLoading(true);
    const yStart = `${year}-01-01`;
    const yEnd   = `${year}-12-31`;

    // ── Members ────────────────────────────────────────────────
    const [
      { count: totalMembers },
      { count: fullMembers  },
      { count: associateMembers },
      { count: adherents    },
      { count: communicants },
      { count: baptisedChildren },
      { count: deceased     },
      { count: newMembers   },
      { count: pendingMembers },
      { data:  members      },
      { data:  groups       },
      { data:  memberGroups },
      { data:  families     },
    ] = await Promise.all([
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Full Member').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Associate Member').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Adherent').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Communicant').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('membership_type','Baptised Child').eq('status','active'),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','deceased'),
      supabase.from('members').select('*',{count:'exact',head:true}).gte('joined_date',yStart).lte('joined_date',yEnd),
      supabase.from('members').select('*',{count:'exact',head:true}).eq('status','pending'),
      supabase.from('members').select('*,districts:district_id(name),families:family_id(name),member_groups(groups(name))').eq('status','active').order('first_name'),
      supabase.from('groups').select('*').order('name'),
      supabase.from('member_groups').select('group_id,groups(name,type)'),
      supabase.from('families').select('*,districts:district_id(name),members(id,first_name,last_name,status)').order('name'),
    ]);

    // ── Contributions — all 3 entry types ─────────────────────
    const [
      { data: allContribs   },
      { data: memberContribs },
      { data: distContribs  },
      { data: sundayContribs },
    ] = await Promise.all([
      supabase.from('contributions').select('*,districts:district_id(name),members(first_name,last_name,member_number,districts:district_id(name))').gte('date',yStart).lte('date',yEnd),
      supabase.from('contributions').select('*,members(first_name,last_name,member_number,districts:district_id(name))').eq('entry_type','member').gte('date',yStart).lte('date',yEnd),
      supabase.from('contributions').select('*,districts:district_id(name)').eq('entry_type','district').gte('date',yStart).lte('date',yEnd),
      supabase.from('contributions').select('*').eq('entry_type','sunday_total').gte('date',yStart).lte('date',yEnd),
    ]);

    // ── Expenses ───────────────────────────────────────────────
    const { data: expenses } = await safe(supabase.from('expenses').select('*,expense_categories(name,type)').gte('date',yStart).lte('date',yEnd));
    const { data: otherIncome } = await safe(supabase.from('income_entries').select('*').gte('date',yStart).lte('date',yEnd));

    // ── Attendance — new structure ─────────────────────────────
    const { data: services } = await supabase
      .from('sunday_services')
      .select('*')
      .gte('date',yStart).lte('date',yEnd)
      .order('date',{ascending:false});

    const { data: communionMarks } = await supabase
      .from('attendance')
      .select('service_id, member_id, status, members(first_name,last_name,member_number,districts:district_id(name))')
      .eq('type','holy_communion')
      .eq('status','present');

    // ── Life events ────────────────────────────────────────────
    const [
      { data: baptisms_tbl    },
      { data: confirms_tbl    },
      { data: weddings_tbl    },
      { data: burials_tbl     },
      { data: memberBaptisms  },
      { data: memberConfirms  },
      { data: memberWeddings  },
      { data: memberBurials   },
    ] = await Promise.all([
      safe(supabase.from('baptisms').select('*').gte('date',yStart).lte('date',yEnd)),
      safe(supabase.from('confirmations').select('*').gte('date',yStart).lte('date',yEnd)),
      safe(supabase.from('weddings').select('*').gte('date',yStart).lte('date',yEnd)),
      safe(supabase.from('burials').select('*').gte('date',yStart).lte('date',yEnd)),
      supabase.from('members').select('id,first_name,last_name,baptism_date,districts:district_id(name),dob,is_child').eq('is_baptised',true).not('baptism_date','is',null).gte('baptism_date',yStart).lte('baptism_date',yEnd),
      supabase.from('members').select('id,first_name,last_name,confirmation_date,districts:district_id(name)').eq('is_confirmed',true).not('confirmation_date','is',null).gte('confirmation_date',yStart).lte('confirmation_date',yEnd),
      supabase.from('members').select('id,first_name,last_name,wedding_date,districts:district_id(name),gender,spouse_name').eq('is_wedded',true).not('wedding_date','is',null).gte('wedding_date',yStart).lte('wedding_date',yEnd),
      supabase.from('members').select('id,first_name,last_name,death_date,districts:district_id(name),dob').eq('is_deceased',true).not('death_date','is',null).gte('death_date',yStart).lte('death_date',yEnd),
    ]);

    // ── Inventory ──────────────────────────────────────────────
    const { data: invCategories } = await safe(supabase.from('inventory_categories').select('*'));
    const { data: invItems      } = await safe(supabase.from('inventory_items').select('*,inventory_categories(name)'));

    // ── Process contributions ──────────────────────────────────
    const sumAmt  = (arr) => (arr||[]).reduce((s,r)=>s+Number(r.amount),0);
    const sumType = (arr,t)=> (arr||[]).filter(r=>r.type===t).reduce((s,r)=>s+Number(r.amount),0);

    const totalContribs   = sumAmt(allContribs);
    const totalTithes     = sumType(allContribs,'Tithe');
    const totalOfferings  = sumType(allContribs,'Offering');
    const totalThanksgiving = sumType(allContribs,'Thanksgiving');
    const totalHarvest    = sumType(allContribs,'Harvest');
    const totalBuilding   = sumType(allContribs,'Building Fund');
    const totalSpecial    = sumType(allContribs,'Special');
    const totalExpense    = sumAmt(expenses);
    const totalOtherIncome= sumAmt(otherIncome);
    const totalIncome     = totalContribs + totalOtherIncome;

    // By district — combine member + district entries
    const byDistrict = {};
    (memberContribs||[]).forEach(c=>{
      const d=c.members?.districts?.name||'Unknown';
      if(!byDistrict[d])byDistrict[d]={tithe:0,offering:0,total:0,fromMember:0,fromDistrict:0};
      byDistrict[d].tithe    += c.type==='Tithe'?Number(c.amount):0;
      byDistrict[d].offering += c.type==='Offering'?Number(c.amount):0;
      byDistrict[d].total    += Number(c.amount);
      byDistrict[d].fromMember += Number(c.amount);
    });
    (distContribs||[]).forEach(c=>{
      const d=c.districts?.name||'Unknown';
      if(!byDistrict[d])byDistrict[d]={tithe:0,offering:0,total:0,fromMember:0,fromDistrict:0};
      byDistrict[d].tithe    += c.type==='Tithe'?Number(c.amount):0;
      byDistrict[d].offering += c.type==='Offering'?Number(c.amount):0;
      byDistrict[d].total    += Number(c.amount);
      byDistrict[d].fromDistrict += Number(c.amount);
    });

    // Sunday totals breakdown
    const sundayByType = {};
    (sundayContribs||[]).forEach(c=>{
      sundayByType[c.type]=(sundayByType[c.type]||0)+Number(c.amount);
    });

    // Monthly trend
    const monthly = MONTHS.map((mon,i)=>{
      const m=String(i+1).padStart(2,'0');
      const rows=(allContribs||[]).filter(c=>c.date?.startsWith(`${year}-${m}`));
      return{
        month:mon.slice(0,3),
        tithe:   rows.filter(c=>c.type==='Tithe').reduce((s,c)=>s+Number(c.amount),0),
        offering:rows.filter(c=>c.type==='Offering').reduce((s,c)=>s+Number(c.amount),0),
        total:   rows.reduce((s,c)=>s+Number(c.amount),0),
      };
    });

    // ── Process attendance ─────────────────────────────────────
    const totalAttendance  = (services||[]).reduce((s,sv)=>s+Number(sv.total_attendance||0),0);
    const totalOfferings_svc=(services||[]).reduce((s,sv)=>s+Number(sv.total_offering||0),0);
    const servicesCount    = (services||[]).length;

    const communionByService = {};
    (communionMarks||[]).forEach(m=>{
      communionByService[m.service_id]=(communionByService[m.service_id]||0)+1;
    });
    const totalCommunion = Object.values(communionByService).reduce((s,v)=>s+v,0);
    const communionServices = (services||[]).filter(sv=>(sv.communion_count||0)>0).length;

    const communionByDistrict = {};
    (communionMarks||[]).forEach(m=>{
      const d=m.members?.districts?.name||'Unknown';
      communionByDistrict[d]=(communionByDistrict[d]||0)+1;
    });

    // ── Merge life events ──────────────────────────────────────
    const merge=(tbl,mems,mapper)=>{
      const ids=new Set((tbl||[]).filter(r=>r._member_id).map(r=>r._member_id));
      return[...(tbl||[]).map(r=>({...r,_source:'manual'})),...(mems||[]).filter(m=>!ids.has(m.id)).map(mapper)];
    };
    const allBaptisms      = merge(baptisms_tbl,memberBaptisms,m=>({id:`m:${m.id}`,full_name:`${m.first_name} ${m.last_name}`,date:m.baptism_date,district:m.districts?.name||'',_source:'member'}));
    const allConfirmations = merge(confirms_tbl,memberConfirms,m=>({id:`m:${m.id}`,full_name:`${m.first_name} ${m.last_name}`,date:m.confirmation_date,district:m.districts?.name||'',_source:'member'}));
    const allWeddings      = merge(weddings_tbl,memberWeddings,m=>({id:`m:${m.id}`,groom_name:m.gender==='Male'?`${m.first_name} ${m.last_name}`:(m.spouse_name||'—'),bride_name:m.gender==='Female'?`${m.first_name} ${m.last_name}`:(m.spouse_name||'—'),date:m.wedding_date,district:m.districts?.name||'',_source:'member'}));
    const allBurials       = merge(burials_tbl, memberBurials, m=>{let age='';if(m.dob&&m.death_date)age=Math.floor((new Date(m.death_date)-new Date(m.dob))/(365.25*86400000));return{id:`m:${m.id}`,full_name:`${m.first_name} ${m.last_name}`,date:m.death_date,district:m.districts?.name||'',age,_source:'member'};});

    // ── Member by district ─────────────────────────────────────
    const membersByDistrict={};
    (members||[]).forEach(m=>{const d=m.districts?.name||'Unknown';membersByDistrict[d]=(membersByDistrict[d]||0)+1;});

    // ── Groups ─────────────────────────────────────────────────
    const byGroup={};
    (memberGroups||[]).forEach(mg=>{const n=mg.groups?.name;if(n)byGroup[n]=(byGroup[n]||0)+1;});

    // ── Expenses by journal ────────────────────────────────────
    const byJournal={};
    (expenses||[]).forEach(e=>{byJournal[e.journal]=(byJournal[e.journal]||0)+Number(e.amount);});

    // ── Inventory ──────────────────────────────────────────────
    const totalInvValue=(invItems||[]).reduce((s,i)=>s+(Number(i.purchase_price)||0)*Number(i.quantity),0);
    const needsRepair=(invItems||[]).filter(i=>['Poor','Needs Repair'].includes(i.condition)).length;

    setData({
      // Members
      totalMembers,fullMembers,associateMembers,adherents,communicants,
      baptisedChildren,deceased,newMembers,pendingMembers,
      membersByDistrict,byGroup,members:members||[],families:families||[],groups:groups||[],
      // Finance — all 3 entry types
      totalContribs,totalTithes,totalOfferings,totalThanksgiving,totalHarvest,totalBuilding,totalSpecial,
      totalExpense,totalOtherIncome,totalIncome,surplus:totalIncome-totalExpense,
      byDistrict,byJournal,monthly,
      // By entry type summary
      memberContribsTotal: sumAmt(memberContribs),
      distContribsTotal:   sumAmt(distContribs),
      sundayContribsTotal: sumAmt(sundayContribs),
      sundayByType,
      memberContribs:memberContribs||[],
      distContribs:distContribs||[],
      sundayContribs:sundayContribs||[],
      allContribs:allContribs||[],
      opex:(expenses||[]).filter(e=>e.expense_categories?.type==='opex').reduce((s,e)=>s+Number(e.amount),0),
      capex:(expenses||[]).filter(e=>e.expense_categories?.type==='capex').reduce((s,e)=>s+Number(e.amount),0),
      // Attendance — new structure
      services:services||[],servicesCount,
      totalAttendance,totalOfferings_svc,
      totalCommunion,communionServices,
      communionByDistrict,
      avgAttendance: servicesCount>0?Math.round(totalAttendance/servicesCount):0,
      // Life events
      allBaptisms,allConfirmations,allWeddings,allBurials,
      // Inventory
      invCategories:invCategories||[],invItems:invItems||[],
      totalInvValue,needsRepair,
    });
    setLoading(false);
  }

  if (loading||!data) return <div style={{textAlign:'center',padding:48,color:'var(--text3)'}}>Loading report data for {year}…</div>;

  return (
    <div>
      <div className="flex-between mb-4 no-print">
        <div>
          <h1 style={{fontSize:20,fontWeight:800}}>📄 Reports</h1>
          <div style={{fontSize:13,color:'var(--text3)',marginTop:2}}>Generate, view and export all church reports</div>
        </div>
        <div className="flex gap-2">
          <select className="form-select" style={{width:'auto'}} value={year} onChange={e=>setYear(Number(e.target.value))}>
            {[2026,2025,2024,2023,2022].map(y=><option key={y}>{y}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={()=>window.print()}>🖨️ Print</button>
        </div>
      </div>

      {/* Section nav */}
      <div className="no-print" style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:20}}>
        {REPORT_SECTIONS.map(s=>(
          <button key={s.key} onClick={()=>setSection(s.key)}
            className={`btn btn-sm ${section===s.key?'btn-blue':'btn-ghost'}`}
            style={{border:section!==s.key?'1.5px solid var(--border)':'',fontSize:12}}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {section==='annual'      && <AnnualSection      data={data} year={year}/>}
      {section==='members'     && <MembersSection      data={data} year={year}/>}
      {section==='financial'   && <FinancialSection    data={data} year={year}/>}
      {section==='attendance'  && <AttendanceSection   data={data} year={year}/>}
      {section==='life_events' && <LifeEventsSection   data={data} year={year}/>}
      {section==='groups'      && <GroupsSection       data={data} year={year}/>}
      {section==='inventory'   && <InventorySection    data={data} year={year}/>}
      {section==='households'  && <HouseholdsSection   data={data} year={year}/>}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────
const KV = ({label,value,bold,color})=>(
  <div style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
    <span style={{color:'var(--text2)'}}>{label}</span>
    <span style={{fontWeight:bold?800:600,color:color||'var(--text)'}}>{value}</span>
  </div>
);
const SecHdr = ({icon,title,count,onPDF,onExcel})=>(
  <div style={{padding:'12px 18px',background:'var(--blue-dark)',color:'white',borderRadius:'12px 12px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
    <span style={{fontWeight:700,fontSize:14}}>{icon} {title}{count!=null?` (${count})`:''}</span>
    <div className="flex gap-2 no-print">
      {onPDF && <button className="btn btn-sm" style={{background:'rgba(255,255,255,.15)',color:'white',border:'1px solid rgba(255,255,255,.3)',fontSize:11}} onClick={onPDF}>📥 PDF</button>}
      {onExcel && <button className="btn btn-sm" style={{background:'rgba(255,255,255,.15)',color:'white',border:'1px solid rgba(255,255,255,.3)',fontSize:11}} onClick={onExcel}>📊 Excel</button>}
    </div>
  </div>
);
const Card=({children,style={}})=>(<div className="card mb-4" style={{overflow:'hidden',...style}}>{children}</div>);
const Body=({children})=>(<div style={{padding:'14px 18px'}}>{children}</div>);
const SBadge=({type})=>{
  const m={member:{l:'👤 Member',bg:'#E3F2FD',c:'#003399'},district:{l:'📍 District',bg:'#E8F5E9',c:'#1B5E20'},sunday_total:{l:'⛪ Sunday Total',bg:'#FFF8E0',c:'#C47A00'}};
  const s=m[type]||{l:type,bg:'#F5F5F5',c:'#546E7A'};
  return <span style={{fontSize:10,padding:'2px 8px',borderRadius:100,background:s.bg,color:s.c,fontWeight:700,whiteSpace:'nowrap'}}>{s.l}</span>;
};

// ══ ANNUAL ════════════════════════════════════════════════════
function AnnualSection({data,year}) {
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <button className="btn btn-primary btn-sm" onClick={()=>generateAnnualReportPDF(data,year)}>📥 Download Full Annual Report PDF</button>
      </div>
      <div className="grid-4 mb-4">
        {[['👥','Active Members',data.totalMembers,'blue'],['💰','Total Income',fmt(data.totalIncome),'green'],['📅','Services',data.servicesCount,'red'],['📖','Life Events',data.allBaptisms.length+data.allConfirmations.length+data.allWeddings.length+data.allBurials.length,'amber']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
      <div className="grid-2 mb-4">
        <Card>
          <SecHdr icon="👥" title={`Membership — ${year}`}/>
          <Body>
            {[['Full Members',data.fullMembers],['Associate Members',data.associateMembers],['Adherents',data.adherents],['Communicants',data.communicants],['Baptised Children',data.baptisedChildren],['New This Year',data.newMembers],['Pending',data.pendingMembers],['Deceased (recorded)',data.deceased]].map(([l,v])=><KV key={l} label={l} value={v}/>)}
          </Body>
        </Card>
        <Card>
          <SecHdr icon="💰" title={`Finance Summary — ${year}`}/>
          <Body>
            {[['Tithes',fmt(data.totalTithes)],['Offerings',fmt(data.totalOfferings)],['Thanksgiving',fmt(data.totalThanksgiving)],['Harvest',fmt(data.totalHarvest)],['Building Fund',fmt(data.totalBuilding)],['Other Income',fmt(data.totalOtherIncome)]].map(([l,v])=><KV key={l} label={l} value={v}/>)}
            <KV label="TOTAL INCOME" value={fmt(data.totalIncome)} bold color="var(--blue-dark)"/>
            <KV label="Total Expenses" value={fmt(data.totalExpense)} color="var(--red)"/>
            <KV label="NET SURPLUS" value={fmt(data.surplus)} bold color={data.surplus>=0?'var(--success)':'var(--red)'}/>
          </Body>
        </Card>
      </div>
      <div className="grid-2 mb-4">
        <Card>
          <SecHdr icon="📋" title={`Attendance — ${year}`}/>
          <Body>
            {[['Services Held',data.servicesCount],['Total Attendance (headcount)',data.totalAttendance],['Average per Service',data.avgAttendance],['Holy Communion Services',data.communionServices],['Total Communicants',data.totalCommunion]].map(([l,v])=><KV key={l} label={l} value={v}/>)}
          </Body>
        </Card>
        <Card>
          <SecHdr icon="📖" title={`Life Events — ${year}`}/>
          <Body>
            {[['💧 Baptisms',data.allBaptisms.length],['✝️ Confirmations',data.allConfirmations.length],['💍 Weddings',data.allWeddings.length],['⚰️ Burials',data.allBurials.length]].map(([l,v])=><KV key={l} label={l} value={v}/>)}
          </Body>
        </Card>
      </div>
      <Card>
        <SecHdr icon="📍" title="Members by District"/>
        <Body>
          <div className="bar-chart">
            {Object.entries(data.membersByDistrict).sort((a,b)=>b[1]-a[1]).map(([d,c])=>(
              <div key={d} className="bar-row">
                <span className="bar-label">{d}</span>
                <div className="bar-track"><div className="bar-fill blue" style={{width:`${Math.round((c/Math.max(...Object.values(data.membersByDistrict),1))*100)}%`}}><span className="bar-fill-val">{c}</span></div></div>
              </div>
            ))}
          </div>
        </Body>
      </Card>
    </div>
  );
}

// ══ MEMBERS ═══════════════════════════════════════════════════
function MembersSection({data,year}) {
  const [distF,setDistF]=useState('');
  const [typeF,setTypeF]=useState('');
  const filtered=data.members.filter(m=>(!distF||m.districts?.name===distF)&&(!typeF||m.membership_type===typeF));
  const districts=[...new Set(data.members.map(m=>m.districts?.name).filter(Boolean))].sort();
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print flex-wrap">
        <button className="btn btn-primary btn-sm" onClick={()=>generateMembersPDF(filtered,`Members Report ${year}`)}>📥 Members PDF</button>
        <button className="btn btn-outline btn-sm" onClick={()=>exportMembersExcel(filtered)}>📊 Members Excel</button>
      </div>
      <div className="grid-4 mb-4">
        {[['👥','Total Active',data.totalMembers,'blue'],['✝️','Full Members',data.fullMembers,'blue'],['🆕','New This Year',data.newMembers,'green'],['⏳','Pending',data.pendingMembers,'amber']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
      <div className="grid-2 mb-4">
        <Card>
          <SecHdr icon="📊" title="By Membership Type"/>
          <Body>{[['Full Member',data.fullMembers],['Associate Member',data.associateMembers],['Adherent',data.adherents],['Communicant',data.communicants],['Baptised Child',data.baptisedChildren]].map(([l,v])=><KV key={l} label={l} value={v}/>)}<KV label="TOTAL" value={data.totalMembers} bold/></Body>
        </Card>
        <Card>
          <SecHdr icon="📍" title="By District"/>
          <Body>
            <div className="bar-chart">
              {Object.entries(data.membersByDistrict).sort((a,b)=>b[1]-a[1]).map(([d,c])=>(
                <div key={d} className="bar-row"><span className="bar-label">{d}</span><div className="bar-track"><div className="bar-fill blue" style={{width:`${Math.round((c/Math.max(...Object.values(data.membersByDistrict),1))*100)}%`}}><span className="bar-fill-val">{c}</span></div></div></div>
              ))}
            </div>
          </Body>
        </Card>
      </div>
      <Card>
        <SecHdr icon="👥" title={`Members List (${filtered.length})`} onPDF={()=>generateMembersPDF(filtered)} onExcel={()=>exportMembersExcel(filtered)}/>
        <div style={{padding:'10px 18px',background:'var(--bg)',display:'flex',gap:10,flexWrap:'wrap'}}>
          <select className="form-select" style={{width:'auto'}} value={distF} onChange={e=>setDistF(e.target.value)}><option value="">All Districts</option>{districts.map(d=><option key={d}>{d}</option>)}</select>
          <select className="form-select" style={{width:'auto'}} value={typeF} onChange={e=>setTypeF(e.target.value)}><option value="">All Types</option>{['Full Member','Associate Member','Adherent','Communicant','Baptised Child'].map(t=><option key={t}>{t}</option>)}</select>
          <span style={{fontSize:12,color:'var(--text3)',alignSelf:'center'}}>{filtered.length} members</span>
        </div>
        <div style={{overflowX:'auto'}}>
          <table>
            <thead><tr><th>No.</th><th>Name</th><th>District</th><th>Type</th><th>Phone</th><th>Status</th><th>Joined</th></tr></thead>
            <tbody>
              {filtered.slice(0,50).map(m=>(
                <tr key={m.id} style={{opacity:m.status==='deceased'?.5:1}}>
                  <td style={{fontSize:11,fontFamily:'monospace',color:'var(--blue-dark)',fontWeight:700}}>{m.member_number||'—'}</td>
                  <td style={{fontWeight:700}}>{m.first_name} {m.last_name}</td>
                  <td style={{fontSize:12}}>{m.districts?.name||'—'}</td>
                  <td><span className={`badge ${m.membership_type==='Full Member'?'badge-blue':'badge-gray'}`} style={{fontSize:10}}>{m.membership_type}</span></td>
                  <td style={{fontSize:12}}>{m.phone||'—'}</td>
                  <td><span className={`badge badge-${m.status}`} style={{fontSize:10}}>{m.status}</span></td>
                  <td style={{fontSize:11,color:'var(--text3)'}}>{fmtDate(m.joined_date)}</td>
                </tr>
              ))}
              {filtered.length>50&&<tr><td colSpan={7} style={{textAlign:'center',padding:12,color:'var(--text3)',fontSize:12}}>Showing first 50 of {filtered.length}. Download Excel/PDF for full list.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ══ FINANCIAL ════════════════════════════════════════════════
function FinancialSection({data,year}) {
  const [month,setMonth]=useState('');
  const [entryF,setEntryF]=useState('');

  const filtered = data.allContribs.filter(c=>{
    const mStr=month?`${year}-${String(month).padStart(2,'0')}`:null;
    const matchMonth=!mStr||c.date?.startsWith(mStr);
    const matchEntry=!entryF||c.entry_type===entryF;
    return matchMonth&&matchEntry;
  });
  const filtTotal=filtered.reduce((s,r)=>s+Number(r.amount),0);
  const monthContribs = month ? data.allContribs.filter(c=>c.date?.startsWith(`${year}-${String(month).padStart(2,'0')}`)) : data.allContribs;
  const monthTotal = monthContribs.reduce((s,c)=>s+Number(c.amount),0);
  const monthTithe = monthContribs.filter(c=>c.type==='Tithe').reduce((s,c)=>s+Number(c.amount),0);
  const monthOffering = monthContribs.filter(c=>c.type==='Offering').reduce((s,c)=>s+Number(c.amount),0);

  return(
    <div>
      <div className="flex gap-2 mb-4 no-print flex-wrap">
        <select className="form-select" style={{width:'auto'}} value={month} onChange={e=>setMonth(e.target.value)}>
          <option value="">Full Year {year}</option>
          {MONTHS.map((m,i)=><option key={i+1} value={i+1}>{m} {year}</option>)}
        </select>
        <button className="btn btn-primary btn-sm" onClick={()=>generateFinancialPDF(monthContribs,{total:monthTotal,tithes:monthTithe,offerings:monthOffering,special:monthContribs.filter(c=>!['Tithe','Offering'].includes(c.type)).reduce((s,c)=>s+Number(c.amount),0)},month?`${MONTHS[month-1]} ${year}`:`Annual ${year}`)}>📥 Financial PDF</button>
        <button className="btn btn-outline btn-sm" onClick={()=>exportFinancialsExcel(monthContribs,year)}>📊 Excel</button>
      </div>

      <div className="grid-4 mb-4">
        {[['💰','Total Income',fmt(data.totalIncome),'green'],['🙏','Tithes',fmt(data.totalTithes),'blue'],['🎁','Offerings',fmt(data.totalOfferings),'amber'],['💸','Expenses',fmt(data.totalExpense),'red']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>

      <div className="grid-3 mb-4">
        {[
          {icon:'👤',label:'Per Member Total',value:data.memberContribsTotal,color:'#003399',bg:'#E3F2FD'},
          {icon:'📍',label:'Per District Total',value:data.distContribsTotal,color:'#1B5E20',bg:'#E8F5E9'},
          {icon:'⛪',label:'Sunday Totals',value:data.sundayContribsTotal,color:'#C47A00',bg:'#FFF8E0'},
        ].map(({icon,label,value,color,bg})=>(
          <div key={label} style={{background:bg,borderRadius:12,padding:'16px 18px',border:`1.5px solid ${color}22`}}>
            <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
            <div style={{fontWeight:700,color,fontSize:16}}>{fmt(value)}</div>
            <div style={{fontSize:12,color:'var(--text3)',marginTop:2}}>{label}</div>
          </div>
        ))}
      </div>

      <div className="grid-2 mb-4">
        <Card>
          <SecHdr icon="📥" title="Income Statement"/>
          <Body>
            {[['Tithes',fmt(data.totalTithes)],['Offerings',fmt(data.totalOfferings)],['Thanksgiving',fmt(data.totalThanksgiving)],['Harvest',fmt(data.totalHarvest)],['Building Fund',fmt(data.totalBuilding)],['Special',fmt(data.totalSpecial)],['Other Income',fmt(data.totalOtherIncome)]].map(([l,v])=><KV key={l} label={l} value={v}/>)}
            <KV label="TOTAL INCOME" value={fmt(data.totalIncome)} bold color="var(--blue-dark)"/>
            <div style={{height:8}}/>
            <KV label="Operating (OPEX)" value={fmt(data.opex)} color="var(--red)"/>
            <KV label="Capital (CAPEX)" value={fmt(data.capex)} color="var(--red)"/>
            <KV label="TOTAL EXPENSES" value={fmt(data.totalExpense)} bold color="var(--red)"/>
            <div style={{height:8}}/>
            <KV label="NET SURPLUS / (DEFICIT)" value={fmt(data.surplus)} bold color={data.surplus>=0?'var(--success)':'var(--red)'}/>
          </Body>
        </Card>

        <Card>
          <SecHdr icon="⛪" title="Sunday Total Contributions by Type"/>
          <Body>
            {Object.entries(data.sundayByType).length===0
              ?<div style={{color:'var(--text3)',fontSize:13}}>No Sunday total entries recorded.</div>
              :Object.entries(data.sundayByType).sort((a,b)=>b[1]-a[1]).map(([t,v])=><KV key={t} label={t} value={fmt(v)}/>)
            }
            <div style={{height:8}}/>
            <KV label="SUNDAY TOTALS" value={fmt(data.sundayContribsTotal)} bold/>
            <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>
              Sunday total entries represent whole-church collections recorded as a lump sum without individual member breakdown.
            </div>
          </Body>
        </Card>
      </div>

      <Card>
        <SecHdr icon="📍" title="Contributions by District (Member + District entries)"/>
        <Body>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{background:'var(--bg)'}}>
                  <th style={{padding:'8px',textAlign:'left'}}>District</th>
                  <th style={{padding:'8px',textAlign:'right'}}>Tithes</th>
                  <th style={{padding:'8px',textAlign:'right'}}>Offerings</th>
                  <th style={{padding:'8px',textAlign:'right'}}>Per Member</th>
                  <th style={{padding:'8px',textAlign:'right'}}>Per District</th>
                  <th style={{padding:'8px',textAlign:'right'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.byDistrict).sort((a,b)=>b[1].total-a[1].total).map(([d,v])=>(
                  <tr key={d} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'8px',fontWeight:600}}>{d}</td>
                    <td style={{padding:'8px',textAlign:'right'}}>{fmt(v.tithe)}</td>
                    <td style={{padding:'8px',textAlign:'right'}}>{fmt(v.offering)}</td>
                    <td style={{padding:'8px',textAlign:'right',fontSize:11,color:'var(--text3)'}}>{fmt(v.fromMember)}</td>
                    <td style={{padding:'8px',textAlign:'right',fontSize:11,color:'var(--text3)'}}>{fmt(v.fromDistrict)}</td>
                    <td style={{padding:'8px',textAlign:'right',fontWeight:700,color:'var(--blue-dark)'}}>{fmt(v.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Body>
      </Card>

      <Card>
        <SecHdr icon="📈" title={`Monthly Contribution Trend — ${year}`}/>
        <Body>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'var(--bg)'}}><th style={{padding:'6px 10px',textAlign:'left'}}>Month</th><th style={{padding:'6px 10px',textAlign:'right'}}>Tithe</th><th style={{padding:'6px 10px',textAlign:'right'}}>Offering</th><th style={{padding:'6px 10px',textAlign:'right'}}>Total</th></tr></thead>
              <tbody>
                {data.monthly.map(m=>(
                  <tr key={m.month} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'6px 10px',fontWeight:600}}>{m.month}</td>
                    <td style={{padding:'6px 10px',textAlign:'right'}}>{m.tithe>0?fmt(m.tithe):'—'}</td>
                    <td style={{padding:'6px 10px',textAlign:'right'}}>{m.offering>0?fmt(m.offering):'—'}</td>
                    <td style={{padding:'6px 10px',textAlign:'right',fontWeight:m.total>0?700:400,color:m.total>0?'var(--blue-dark)':'var(--text3)'}}>{m.total>0?fmt(m.total):'—'}</td>
                  </tr>
                ))}
                <tr style={{background:'var(--blue-light)',fontWeight:800}}>
                  <td style={{padding:'6px 10px'}}>TOTAL</td>
                  <td style={{padding:'6px 10px',textAlign:'right'}}>{fmt(data.totalTithes)}</td>
                  <td style={{padding:'6px 10px',textAlign:'right'}}>{fmt(data.totalOfferings)}</td>
                  <td style={{padding:'6px 10px',textAlign:'right'}}>{fmt(data.totalContribs)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Body>
      </Card>

      <Card>
        <SecHdr icon="📋" title={`All Contributions — ${year}`} sub={`${filtered.length} records`} onPDF={()=>generateFinancialPDF(filtered,{total:filtTotal,tithes:0,offerings:0,special:0},`${year} Contributions`)} onExcel={()=>exportFinancialsExcel(filtered,year)}/>
        <div style={{padding:'10px 18px',background:'var(--bg)',display:'flex',gap:10,flexWrap:'wrap'}}>
          <select className="form-select" style={{width:'auto'}} value={month} onChange={e=>setMonth(e.target.value)}>
            <option value="">All Months</option>
            {MONTHS.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select className="form-select" style={{width:'auto'}} value={entryF} onChange={e=>setEntryF(e.target.value)}>
            <option value="">All Entry Types</option>
            <option value="member">👤 Per Member</option>
            <option value="district">📍 Per District</option>
            <option value="sunday_total">⛪ Sunday Total</option>
          </select>
          <span style={{fontSize:12,color:'var(--text3)',alignSelf:'center'}}>{filtered.length} records · {fmt(filtTotal)}</span>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'var(--bg)'}}><th style={{padding:'8px',textAlign:'left'}}>Date</th><th>Entry Type</th><th>Member / District</th><th>Type</th><th>Amount</th><th>Method</th></tr></thead>
            <tbody>
              {filtered.slice(0,60).map(r=>(
                <tr key={r.id} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{whiteSpace:'nowrap',padding:'6px 8px'}}>{fmtDate(r.date)}</td>
                  <td><SBadge type={r.entry_type}/></td>
                  <td style={{fontSize:12,fontWeight:600}}>
                    {r.entry_type==='member'?`${r.members?.first_name||''} ${r.members?.last_name||''} (${r.members?.member_number||''})`
                    :r.entry_type==='district'?`📍 ${r.districts?.name||'—'}`
                    :'⛪ Whole Church'}
                  </td>
                  <td><span className={`badge badge-${r.type==='Tithe'?'blue':'green'}`} style={{fontSize:10}}>{r.type}</span></td>
                  <td style={{fontWeight:700,color:'var(--blue-dark)'}}>{fmt(r.amount)}</td>
                  <td style={{fontSize:11,color:'var(--text3)'}}>{r.payment_method||'cash'}</td>
                </tr>
              ))}
              {filtered.length>60&&<tr><td colSpan={6} style={{textAlign:'center',padding:12,color:'var(--text3)',fontSize:12}}>Showing first 60 of {filtered.length}. Download Excel/PDF for full list.</td></tr>}
              {filtered.length>0&&<tr style={{background:'var(--blue-light)',fontWeight:700}}><td colSpan={4}>TOTAL</td><td>{fmt(filtTotal)}</td><td></td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ══ ATTENDANCE ════════════════════════════════════════════════
function AttendanceSection({data,year}) {
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <button className="btn btn-primary btn-sm" onClick={()=>generateAttendancePDF(data.services)}>📥 Attendance PDF</button>
        <button className="btn btn-outline btn-sm" onClick={()=>exportAttendanceExcel(data.services)}>📊 Excel</button>
      </div>
      <div className="grid-4 mb-4">
        {[['📅','Services',data.servicesCount,'blue'],['👥','Total Attendance',data.totalAttendance,'green'],['📊','Avg per Service',data.avgAttendance,'amber'],['🍞','Communions',data.totalCommunion,'red']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>

      <div style={{background:'var(--blue-light)',border:'1px solid #90CAF9',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:12,color:'var(--blue-dark)'}}>
        <strong>How attendance is tracked:</strong> Total headcount is recorded per service (entered manually on the service record).
        Holy Communion tracks individual Full Members marked present per service.
      </div>

      <Card>
        <SecHdr icon="📅" title={`Sunday Services — ${year}`} sub={`${data.servicesCount} services`} onPDF={()=>generateAttendancePDF(data.services)} onExcel={()=>exportAttendanceExcel(data.services)}/>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr><th>Date</th><th>Type</th><th>Sermon</th><th>Preacher</th><th>Headcount</th><th>Offering (KES)</th><th>Communion</th></tr></thead>
            <tbody>
              {data.services.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>No services recorded for {year}</td></tr>}
              {data.services.map(svc=>(
                <tr key={svc.id} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{whiteSpace:'nowrap',fontWeight:700}}>{fmtDate(svc.date)}</td>
                  <td><span className="badge badge-blue" style={{fontSize:10}}>{svc.service_type||'Sunday Service'}</span></td>
                  <td style={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{svc.sermon_title||'—'}</td>
                  <td style={{fontSize:12}}>{svc.preacher||'—'}</td>
                  <td style={{fontWeight:800,textAlign:'center',color:'var(--blue-dark)'}}>{svc.total_attendance||'—'}</td>
                  <td style={{fontWeight:600}}>{svc.total_offering?Number(svc.total_offering).toLocaleString():'—'}</td>
                  <td style={{textAlign:'center'}}>
                    {(svc.communion_count||0)>0
                      ?<span style={{fontWeight:700,color:'var(--blue-dark)',background:'var(--blue-light)',padding:'2px 10px',borderRadius:100,fontSize:12}}>🍞 {svc.communion_count}</span>
                      :<span style={{color:'var(--text3)',fontSize:12}}>—</span>}
                  </td>
                </tr>
              ))}
              {data.services.length>0&&(
                <tr style={{background:'var(--blue-dark)',color:'white',fontWeight:700}}>
                  <td colSpan={4}>TOTALS</td>
                  <td style={{textAlign:'center'}}>{data.totalAttendance}</td>
                  <td>{data.totalOfferings_svc>0?data.totalOfferings_svc.toLocaleString():'—'}</td>
                  <td style={{textAlign:'center'}}>🍞 {data.totalCommunion}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {Object.keys(data.communionByDistrict).length>0&&(
        <Card>
          <SecHdr icon="🍞" title="Holy Communion — By District"/>
          <Body>
            <div className="bar-chart">
              {Object.entries(data.communionByDistrict).sort((a,b)=>b[1]-a[1]).map(([d,c])=>(
                <div key={d} className="bar-row">
                  <span className="bar-label">{d}</span>
                  <div className="bar-track"><div className="bar-fill blue" style={{width:`${Math.round((c/Math.max(...Object.values(data.communionByDistrict),1))*100)}%`}}><span className="bar-fill-val">{c}</span></div></div>
                </div>
              ))}
            </div>
            <div style={{marginTop:12,fontSize:12,color:'var(--text3)'}}>
              Total communicants across all services: <strong>{data.totalCommunion}</strong>
            </div>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ══ LIFE EVENTS ═══════════════════════════════════════════════
function LifeEventsSection({data,year}) {
  const [tab,setTab]=useState('baptisms');
  const tabData={baptisms:data.allBaptisms,confirmations:data.allConfirmations,weddings:data.allWeddings,burials:data.allBurials};
  const SrcBadge=({src})=>src==='member'
    ?<span style={{fontSize:10,padding:'2px 7px',borderRadius:100,background:'#E3F2FD',color:'#003399',fontWeight:700}}>👤 Member</span>
    :<span style={{fontSize:10,padding:'2px 7px',borderRadius:100,background:'#F3E5F5',color:'#6A1B9A',fontWeight:700}}>✏️ Manual</span>;
  return(
    <div>
      <div className="grid-4 mb-4">
        {[['💧',`Baptisms`,data.allBaptisms.length,'blue'],['✝️','Confirmations',data.allConfirmations.length,'blue'],['💍','Weddings',data.allWeddings.length,'amber'],['⚰️','Burials',data.allBurials.length,'red']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l} {year}</div></div>
        ))}
      </div>
      <div className="tabs">
        {[['baptisms','💧 Baptisms'],['confirmations','✝️ Confirmations'],['weddings','💍 Weddings'],['burials','⚰️ Burials']].map(([k,l])=>(
          <button key={k} className={`tab${tab===k?' active':''}`} onClick={()=>setTab(k)}>{l} ({tabData[k].length})</button>
        ))}
      </div>
      <Card>
        <SecHdr icon="" title={`${tab.charAt(0).toUpperCase()+tab.slice(1)} — ${year}`} count={tabData[tab].length}
          onPDF={()=>generateTrackingPDF(tabData[tab],tab,year)} onExcel={()=>exportTrackingExcel?.(tabData[tab],tab,year)}/>
        <div style={{overflowX:'auto'}}>
          {tab==='baptisms'&&<table style={{width:'100%'}}><thead><tr><th>Source</th><th>Name</th><th>Date</th><th>District</th></tr></thead><tbody>{tabData.baptisms.length===0&&<tr><td colSpan={4} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>None recorded</td></tr>}{tabData.baptisms.map(r=><tr key={r.id} style={{background:r._source==='member'?'rgba(3,169,244,0.04)':'white'}}><td><SrcBadge src={r._source}/></td><td style={{fontWeight:700}}>{r.full_name}</td><td>{fmtDate(r.date)}</td><td>{r.district||'—'}</td></tr>)}</tbody></table>}
          {tab==='confirmations'&&<table><thead><tr><th>Source</th><th>Name</th><th>Date</th><th>District</th></tr></thead><tbody>{tabData.confirmations.length===0&&<tr><td colSpan={4} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>None recorded</td></tr>}{tabData.confirmations.map(r=><tr key={r.id} style={{background:r._source==='member'?'rgba(3,169,244,0.04)':'white'}}><td><SrcBadge src={r._source}/></td><td style={{fontWeight:700}}>{r.full_name}</td><td>{fmtDate(r.date)}</td><td>{r.district||'—'}</td></tr>)}</tbody></table>}
          {tab==='weddings'&&<table><thead><tr><th>Source</th><th>Groom</th><th>Bride</th><th>Date</th><th>District</th></tr></thead><tbody>{tabData.weddings.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>None recorded</td></tr>}{tabData.weddings.map(r=><tr key={r.id} style={{background:r._source==='member'?'rgba(3,169,244,0.04)':'white'}}><td><SrcBadge src={r._source}/></td><td style={{fontWeight:700}}>{r.groom_name}</td><td style={{fontWeight:700}}>{r.bride_name}</td><td>{fmtDate(r.date)}</td><td>{r.district||'—'}</td></tr>)}</tbody></table>}
          {tab==='burials'&&<table><thead><tr><th>Source</th><th>Name</th><th>Date</th><th>District</th><th>Age</th></tr></thead><tbody>{tabData.burials.length===0&&<tr><td colSpan={5} style={{textAlign:'center',padding:24,color:'var(--text3)'}}>None recorded</td></tr>}{tabData.burials.map(r=><tr key={r.id} style={{background:r._source==='member'?'rgba(3,169,244,0.04)':'white'}}><td><SrcBadge src={r._source}/></td><td style={{fontWeight:700}}>{r.full_name}</td><td>{fmtDate(r.date)}</td><td>{r.district||'—'}</td><td>{r.age||'—'}</td></tr>)}</tbody></table>}
        </div>
      </Card>
    </div>
  );
}

// ══ GROUPS ════════════════════════════════════════════════════
function GroupsSection({data}) {
  const exportGroupsExcel = () => {
    // simple export – convert byGroup to array
    const rows = Object.entries(data.byGroup).map(([name,count])=>({Group:name,Members:count}));
    import('xlsx').then(XLSX=>{
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Groups');
      XLSX.writeFile(wb, `Groups_Report.xlsx`);
    });
  };
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <button className="btn btn-outline btn-sm" onClick={exportGroupsExcel}>📊 Excel</button>
      </div>
      <div className="grid-3 mb-4">
        {[['🙏','Total Groups',data.groups.length,'blue'],['👥','Total Memberships',Object.values(data.byGroup).reduce((s,v)=>s+v,0),'green'],['📊','Avg per Group',Math.round(Object.values(data.byGroup).reduce((s,v)=>s+v,0)/Math.max(data.groups.length,1)),'amber']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
      <Card>
        <SecHdr icon="🙏" title="Groups & Ministries — Membership"/>
        <Body>
          <div className="bar-chart">
            {Object.entries(data.byGroup).sort((a,b)=>b[1]-a[1]).map(([g,c])=>(
              <div key={g} className="bar-row"><span className="bar-label" style={{width:140,fontSize:11}}>{g}</span><div className="bar-track"><div className="bar-fill blue" style={{width:`${Math.round((c/Math.max(...Object.values(data.byGroup),1))*100)}%`}}><span className="bar-fill-val">{c}</span></div></div></div>
            ))}
          </div>
        </Body>
      </Card>
    </div>
  );
}

// ══ INVENTORY ════════════════════════════════════════════════
function InventorySection({data}) {
  const COND_COLOR={'Excellent':'#1A7A32','Good':'#2E7D32','Fair':'#C47A00','Poor':'#CC0000','Needs Repair':'#CC0000','Disposed':'#546E7A'};
  const exportInventoryExcel = () => {
    const rows = data.invItems.map(i=>({
      Category: i.inventory_categories?.name||'',
      Item: i.name,
      Subcategory: i.subcategory||'',
      Quantity: i.quantity,
      Unit: i.unit||'pcs',
      Condition: i.condition,
      Location: i.location||'',
      'Purchase Price': i.purchase_price||'',
    }));
    import('xlsx').then(XLSX=>{
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
      XLSX.writeFile(wb, `Inventory_Report.xlsx`);
    });
  };
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <button className="btn btn-outline btn-sm" onClick={exportInventoryExcel}>📊 Excel</button>
      </div>
      <div className="grid-3 mb-4">
        {[['📦','Categories',data.invCategories.length,'blue'],['🔢','Total Items',data.invItems.length,'green'],['⚠️','Need Attention',data.needsRepair,'red']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
      {data.invCategories.map(cat=>{
        const items=data.invItems.filter(i=>i.category_id===cat.id);
        if(!items.length)return null;
        return(
          <Card key={cat.id}>
            <SecHdr icon={cat.icon} title={cat.name} sub={`${items.length} items`}/>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%'}}>
                <thead><tr><th>Item</th><th>Qty</th><th>Condition</th><th>Location</th><th>Unit Price</th></tr></thead>
                <tbody>
                  {items.map(item=>(
                    <tr key={item.id}>
                      <td style={{fontWeight:700}}>{item.name}</td>
                      <td style={{fontWeight:800,color:'var(--blue-dark)',textAlign:'center'}}>{item.quantity}</td>
                      <td><span style={{fontSize:11,padding:'2px 8px',borderRadius:100,background:(COND_COLOR[item.condition]||'#546E7A')+'22',color:COND_COLOR[item.condition]||'#546E7A',fontWeight:700}}>{item.condition}</span></td>
                      <td style={{fontSize:12}}>{item.location||'—'}</td>
                      <td style={{fontSize:12}}>{item.purchase_price?`KES ${Number(item.purchase_price).toLocaleString()}`:'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ══ HOUSEHOLDS ═══════════════════════════════════════════════
function HouseholdsSection({data}) {
  const byDist={};
  data.families.forEach(f=>{const d=f.districts?.name||'Unknown';byDist[d]=(byDist[d]||0)+1;});
  const exportHouseholdsExcel = () => {
    const rows = data.families.map(f=>({
      Household: f.name,
      District: f.districts?.name||'',
      Members: f.members?.length||0,
      Active: (f.members||[]).filter(m=>m.status==='active').length,
    }));
    import('xlsx').then(XLSX=>{
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Households');
      XLSX.writeFile(wb, `Households_Report.xlsx`);
    });
  };
  return(
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <button className="btn btn-outline btn-sm" onClick={exportHouseholdsExcel}>📊 Excel</button>
      </div>
      <div className="grid-3 mb-4">
        {[['🏠','Total Households',data.families.length,'blue'],['👥','Linked Members',data.families.reduce((s,f)=>s+(f.members||[]).length,0),'green'],['📊','Avg/Household',(data.families.reduce((s,f)=>s+(f.members||[]).length,0)/Math.max(data.families.length,1)).toFixed(1),'amber']].map(([ic,l,v,c])=>(
          <div key={l} className="stat-card"><div className={`stat-icon ${c}`}>{ic}</div><div className="stat-value">{v}</div><div className="stat-label">{l}</div></div>
        ))}
      </div>
      <div className="grid-2 mb-4">
        <Card>
          <SecHdr icon="📍" title="By District"/>
          <Body>
            <div className="bar-chart">
              {Object.entries(byDist).sort((a,b)=>b[1]-a[1]).map(([d,c])=>(
                <div key={d} className="bar-row"><span className="bar-label">{d}</span><div className="bar-track"><div className="bar-fill blue" style={{width:`${Math.round((c/Math.max(...Object.values(byDist),1))*100)}%`}}><span className="bar-fill-val">{c}</span></div></div></div>
              ))}
            </div>
          </Body>
        </Card>
        <Card>
          <SecHdr icon="🏆" title="Largest Households"/>
          <Body>
            {[...data.families].sort((a,b)=>(b.members?.length||0)-(a.members?.length||0)).slice(0,8).map((f,i)=>(
              <KV key={f.id} label={`${i+1}. 🏠 ${f.name} (${f.districts?.name||'—'})`} value={`${f.members?.length||0} members`}/>
            ))}
          </Body>
        </Card>
      </div>
      <Card>
        <SecHdr icon="🏠" title={`All Households (${data.families.length})`}/>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%'}}>
            <thead><tr><th>Household</th><th>District</th><th>Members</th><th>Active</th></tr></thead>
            <tbody>
              {data.families.sort((a,b)=>a.name.localeCompare(b.name)).map(f=>(
                <tr key={f.id}>
                  <td style={{fontWeight:700}}>🏠 {f.name}</td>
                  <td style={{fontSize:12}}>{f.districts?.name||'—'}</td>
                  <td style={{fontWeight:700,textAlign:'center'}}>{f.members?.length||0}</td>
                  <td style={{textAlign:'center',color:'var(--success)',fontWeight:600}}>{(f.members||[]).filter(m=>m.status==='active').length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}